// File Location: src/pages/admin/AdminSettings.jsx

import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Loader,
  AlertTriangle,
  Info,
  Database,
  Shield,
  Globe,
  Users,
  FileText,
  Briefcase,
  Mail,
  Bell,
  Palette,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Download,
  Upload
} from 'lucide-react';
import { 
  getSystemSettings, 
  updateSystemSettings,
  getAvailableServices,
  updateAvailableServices,
  addService,
  deleteService,
  updateService
} from '../../services/settingsService';
import { useAuth } from '../../context/AuthContext';

const AdminSettings = () => {
  const { userData } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('services');
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    category: '',
    isActive: true,
    requiresApproval: true
  });
  const [editingService, setEditingService] = useState(null);
  const [showAddService, setShowAddService] = useState(false);

  // Settings tabs configuration
  const settingsTabs = [
    {
      id: 'services',
      label: 'Available Services',
      icon: Briefcase,
      description: 'Manage services available for lead generators'
    },
    {
      id: 'general',
      label: 'General Settings',
      icon: Settings,
      description: 'Basic website configuration'
    },
    {
      id: 'email',
      label: 'Email Settings',
      icon: Mail,
      description: 'Email templates and configurations'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'System notification settings'
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      description: 'Security and access controls'
    },
    {
      id: 'appearance',
      label: 'Appearance',
      icon: Palette,
      description: 'Theme and branding settings'
    }
  ];

  useEffect(() => {
    loadSettings();
    loadServices();
  }, []);

  const loadSettings = async () => {
    try {
      const result = await getSystemSettings();
      if (result.success) {
        setSettings(result.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadServices = async () => {
    setLoading(true);
    try {
      const result = await getAvailableServices();
      if (result.success) {
        setServices(result.services);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (section, data) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const result = await updateSystemSettings(section, data, userData.uid);
      if (result.success) {
        setSettings(prev => ({ ...prev, [section]: data }));
        alert('Settings saved successfully!');
      } else {
        alert('Error saving settings: ' + result.error);
      }
    } catch (error) {
      alert('Failed to save settings');
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) return;

    setSaving(prev => ({ ...prev, addService: true }));
    try {
      const result = await addService({
        ...newService,
        createdBy: userData.uid,
        createdAt: new Date()
      });

      if (result.success) {
        setServices(prev => [...prev, result.service]);
        setNewService({
          name: '',
          description: '',
          category: '',
          isActive: true,
          requiresApproval: true
        });
        setShowAddService(false);
        alert('Service added successfully!');
      } else {
        alert('Error adding service: ' + result.error);
      }
    } catch (error) {
      alert('Failed to add service');
    } finally {
      setSaving(prev => ({ ...prev, addService: false }));
    }
  };

  const handleUpdateService = async (serviceId, updatedData) => {
    setSaving(prev => ({ ...prev, [serviceId]: true }));
    try {
      const result = await updateService(serviceId, {
        ...updatedData,
        updatedBy: userData.uid,
        updatedAt: new Date()
      });

      if (result.success) {
        setServices(prev => prev.map(service => 
          service.id === serviceId ? { ...service, ...updatedData } : service
        ));
        setEditingService(null);
        alert('Service updated successfully!');
      } else {
        alert('Error updating service: ' + result.error);
      }
    } catch (error) {
      alert('Failed to update service');
    } finally {
      setSaving(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const handleDeleteService = async (serviceId) => {
    // if (!confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
    //   return;
    // }

    setSaving(prev => ({ ...prev, [serviceId]: true }));
    try {
      const result = await deleteService(serviceId, userData.uid);
      if (result.success) {
        setServices(prev => prev.filter(service => service.id !== serviceId));
        alert('Service deleted successfully!');
      } else {
        alert('Error deleting service: ' + result.error);
      }
    } catch (error) {
      alert('Failed to delete service');
    } finally {
      setSaving(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const renderServicesTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Available Services</h3>
          <p className="text-sm text-gray-600">
            Manage services that lead generators can select during registration
          </p>
        </div>
        <button
          onClick={() => setShowAddService(true)}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </button>
      </div>

      {/* Services List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <Loader className="h-6 w-6 animate-spin text-teal-500 mx-auto mb-2" />
            <p className="text-gray-600">Loading services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="p-8 text-center">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h4>
            <p className="text-gray-600 mb-4">Get started by adding your first service</p>
            <button
              onClick={() => setShowAddService(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Service
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {services.map((service) => (
              <div key={service.id} className="p-6">
                {editingService === service.id ? (
                  <EditServiceForm
                    service={service}
                    onSave={(data) => handleUpdateService(service.id, data)}
                    onCancel={() => setEditingService(null)}
                    saving={saving[service.id]}
                  />
                ) : (
                  <ServiceCard
                    service={service}
                    onEdit={() => setEditingService(service.id)}
                    onDelete={() => handleDeleteService(service.id)}
                    onToggleStatus={(isActive) => handleUpdateService(service.id, { isActive })}
                    saving={saving[service.id]}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Service Modal */}
      {showAddService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Service</h3>
              <button
                onClick={() => setShowAddService(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name *
                </label>
                <input
                  type="text"
                  value={newService.name}
                  onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Enter service name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newService.description}
                  onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Describe the service"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={newService.category}
                  onChange={(e) => setNewService(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Select Category</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Finance">Finance</option>
                  <option value="Documentation">Documentation</option>
                  <option value="Verification">Verification</option>
                  <option value="Processing">Processing</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newService.isActive}
                    onChange={(e) => setNewService(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active (available for selection)</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newService.requiresApproval}
                    onChange={(e) => setNewService(prev => ({ ...prev, requiresApproval: e.target.checked }))}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Requires admin approval</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddService(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddService}
                disabled={!newService.name.trim() || saving.addService}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {saving.addService ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Service
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">General Settings</h3>
        <p className="text-sm text-gray-600">Basic website configuration and information</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <GeneralSettingsForm
          settings={settings.general || {}}
          onSave={(data) => handleSaveSettings('general', data)}
          saving={saving.general}
        />
      </div>
    </div>
  );

  const renderEmailTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Email Settings</h3>
        <p className="text-sm text-gray-600">Configure email templates and SMTP settings</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <EmailSettingsForm
          settings={settings.email || {}}
          onSave={(data) => handleSaveSettings('email', data)}
          saving={saving.email}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Settings</h1>
              <p className="text-gray-600 mt-1">Manage system-wide configurations and preferences</p>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  loadSettings();
                  loadServices();
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Settings Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Settings Categories</h3>
              <nav className="space-y-2">
                {settingsTabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-teal-50 text-teal-700 border border-teal-200'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-3" />
                      <div className="text-left">
                        <div>{tab.label}</div>
                        <div className="text-xs text-gray-500">{tab.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            {activeTab === 'services' && renderServicesTab()}
            {activeTab === 'general' && renderGeneralTab()}
            {activeTab === 'email' && renderEmailTab()}
            {activeTab === 'notifications' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Notifications Settings</h3>
                <p className="text-gray-600">Configure system notification preferences</p>
                <p className="text-sm text-gray-500 mt-2">Coming soon...</p>
              </div>
            )}
            {activeTab === 'security' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Security Settings</h3>
                <p className="text-gray-600">Manage security and access controls</p>
                <p className="text-sm text-gray-500 mt-2">Coming soon...</p>
              </div>
            )}
            {activeTab === 'appearance' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <Palette className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Appearance Settings</h3>
                <p className="text-gray-600">Customize theme and branding</p>
                <p className="text-sm text-gray-500 mt-2">Coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, onEdit, onDelete, onToggleStatus, saving }) => (
  <div className="flex items-center justify-between">
    <div className="flex-1">
      <div className="flex items-center space-x-3 mb-2">
        <h4 className="text-lg font-medium text-gray-900">{service.name}</h4>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          service.isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {service.isActive ? 'Active' : 'Inactive'}
        </span>
        {service.category && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {service.category}
          </span>
        )}
      </div>
      {service.description && (
        <p className="text-sm text-gray-600 mb-2">{service.description}</p>
      )}
      <div className="flex items-center space-x-4 text-xs text-gray-500">
        <span>Created: {service.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
        {service.requiresApproval && (
          <span className="flex items-center">
            <Shield className="h-3 w-3 mr-1" />
            Requires Approval
          </span>
        )}
      </div>
    </div>

    <div className="flex items-center space-x-2 ml-4">
      <button
        onClick={() => onToggleStatus(!service.isActive)}
        disabled={saving}
        className={`p-2 rounded-lg transition-colors ${
          service.isActive 
            ? 'text-yellow-600 hover:bg-yellow-50' 
            : 'text-green-600 hover:bg-green-50'
        }`}
        title={service.isActive ? 'Deactivate' : 'Activate'}
      >
        {service.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      
      <button
        onClick={onEdit}
        disabled={saving}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Edit Service"
      >
        <Edit3 className="h-4 w-4" />
      </button>
      
      <button
        onClick={onDelete}
        disabled={saving}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete Service"
      >
        {saving ? (
          <Loader className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </button>
    </div>
  </div>
);

// Edit Service Form Component
const EditServiceForm = ({ service, onSave, onCancel, saving }) => {
  const [editedService, setEditedService] = useState(service);

  const handleSave = () => {
    onSave(editedService);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Service Name</label>
        <input
          type="text"
          value={editedService.name}
          onChange={(e) => setEditedService(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={editedService.description || ''}
          onChange={(e) => setEditedService(prev => ({ ...prev, description: e.target.value }))}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
        <select
          value={editedService.category || ''}
          onChange={(e) => setEditedService(prev => ({ ...prev, category: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        >
          <option value="">Select Category</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Insurance">Insurance</option>
          <option value="Finance">Finance</option>
          <option value="Documentation">Documentation</option>
          <option value="Verification">Verification</option>
          <option value="Processing">Processing</option>
        </select>
      </div>

      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={editedService.isActive}
            onChange={(e) => setEditedService(prev => ({ ...prev, isActive: e.target.checked }))}
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <span className="ml-2 text-sm text-gray-700">Active</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={editedService.requiresApproval}
            onChange={(e) => setEditedService(prev => ({ ...prev, requiresApproval: e.target.checked }))}
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <span className="ml-2 text-sm text-gray-700">Requires approval</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// General Settings Form Component
const GeneralSettingsForm = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    siteName: '',
    siteDescription: '',
    supportEmail: '',
    supportPhone: '',
    address: '',
    ...settings
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
          <input
            type="text"
            value={formData.siteName}
            onChange={(e) => setFormData(prev => ({ ...prev, siteName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Your site name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
          <input
            type="email"
            value={formData.supportEmail}
            onChange={(e) => setFormData(prev => ({ ...prev, supportEmail: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="support@example.com"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
        <textarea
          value={formData.siteDescription}
          onChange={(e) => setFormData(prev => ({ ...prev, siteDescription: e.target.value }))}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          placeholder="Brief description of your platform"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Support Phone</label>
          <input
            type="text"
            value={formData.supportPhone}
            onChange={(e) => setFormData(prev => ({ ...prev, supportPhone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
          <input
            type="text"
            value={formData.businessHours || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, businessHours: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="9 AM - 6 PM (Mon-Fri)"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
        <textarea
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          rows="2"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          placeholder="Your business address"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save General Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Email Settings Form Component
const EmailSettingsForm = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    enableEmailNotifications: true,
    welcomeEmailTemplate: '',
    approvalEmailTemplate: '',
    rejectionEmailTemplate: '',
    ...settings
  });

  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h4 className="text-lg font-medium text-gray-900">SMTP Configuration</h4>
        <p className="text-sm text-gray-600">Configure your email server settings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
          <input
            type="text"
            value={formData.smtpHost}
            onChange={(e) => setFormData(prev => ({ ...prev, smtpHost: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="smtp.gmail.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
          <input
            type="text"
            value={formData.smtpPort}
            onChange={(e) => setFormData(prev => ({ ...prev, smtpPort: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="587"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
          <input
            type="text"
            value={formData.smtpUser}
            onChange={(e) => setFormData(prev => ({ ...prev, smtpUser: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="your-email@gmail.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={formData.smtpPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, smtpPassword: e.target.value }))}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Your app password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Email Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
            <input
              type="email"
              value={formData.fromEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, fromEmail: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="noreply@yoursite.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
            <input
              type="text"
              value={formData.fromName}
              onChange={(e) => setFormData(prev => ({ ...prev, fromName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Your Company Name"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.enableEmailNotifications}
              onChange={(e) => setFormData(prev => ({ ...prev, enableEmailNotifications: e.target.checked }))}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="ml-2 text-sm text-gray-700">Enable automatic email notifications</span>
          </label>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Email Templates</h4>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Welcome Email Template</label>
            <textarea
              value={formData.welcomeEmailTemplate}
              onChange={(e) => setFormData(prev => ({ ...prev, welcomeEmailTemplate: e.target.value }))}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Welcome email content... Use {{name}} for personalization"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Approval Email Template</label>
            <textarea
              value={formData.approvalEmailTemplate}
              onChange={(e) => setFormData(prev => ({ ...prev, approvalEmailTemplate: e.target.value }))}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Application approval email content..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Email Template</label>
            <textarea
              value={formData.rejectionEmailTemplate}
              onChange={(e) => setFormData(prev => ({ ...prev, rejectionEmailTemplate: e.target.value }))}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Application rejection email content..."
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {saving ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Email Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default AdminSettings;